import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import { withLineBreaks } from '../../../../helpers/text';

// The block opener: optional eyebrow + header + description, with the CTA
// slotted in so its markup and analytics stay owned by the block.
//
// Two layout modes:
// - Default: the host is the intro box (flex column) occupying one grid
//   cell — blocks place it with `block-intro { grid-column/grid-row }`.
// - `split` attribute: the host box vanishes (display: contents) and the
//   children participate directly in the block grid; blocks place each via
//   ::part(header) / ::part(description) / ::part(eyebrow).
//
// Knobs: --block-intro-gap. Instance type/placement overrides go through the
// parts. `header-tag` picks h1 for hero-scale blocks (default h2).
class BlockIntro extends LitElement {
  static get properties() {
    return {
      header: { type: String },
      description: { type: String },
      eyebrow: { type: String },
      headerTag: { type: String, attribute: 'header-tag' },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        :host {
          display: flex;
          flex-direction: column;
          gap: var(
            --block-intro-gap,
            calc(30rem / var(--default-rem-px-count))
          );
          min-width: 0;
        }

        :host([split]) {
          display: contents;
        }

        /* Inert wrapper around description + slotted CTA; blocks that pair
           them on one row style it via ::part(body). */
        .body {
          display: contents;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.header = '';
    this.description = '';
    this.eyebrow = '';
    this.headerTag = 'h2';
  }

  render() {
    return html`
      ${this.eyebrowHTML} ${this.headerHTML}
      <div part="body" class="body">
        ${this.descriptionHTML}
        <slot></slot>
      </div>
    `;
  }

  get eyebrowHTML() {
    if (!this.eyebrow) return html``;
    return html`
      <p part="eyebrow" class="eyebrow type-body-small">
        ${unsafeHTML(withLineBreaks(this.eyebrow))}
      </p>
    `;
  }

  get headerHTML() {
    if (!this.header) return html``;
    const text = unsafeHTML(withLineBreaks(this.header));
    return this.headerTag === 'h1'
      ? html`
          <h1 part="header" class="header">${text}</h1>
        `
      : html`
          <h2 part="header" class="header">${text}</h2>
        `;
  }

  get descriptionHTML() {
    if (!this.description) return html``;
    return html`
      <p part="description" class="description">
        ${unsafeHTML(withLineBreaks(this.description))}
      </p>
    `;
  }
}

customElements.define('block-intro', BlockIntro);

export { BlockIntro };
